import React, { useRef, useState, useCallback, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { dateOnlyBodyTemplate } from '../../../../../utils/dateUtils';
import { useLanguage } from '../../../../../contexts/LanguageContext';

const LaborCatalogTable = ({ data, loading, onPage, lazyParams, totalCount, onEdit, onDelete, th }) => {
    const dataTableRef = useRef(null);
    const columnsRef = useRef(null);

    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);
    const { getCurrentLanguageId, getTranslationText } = useLanguage(); // Dil context'i
    const normalizedData = useMemo(() => {
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.items)) return data.items;
        return [];
    }, [data]);
    const statusBodyTemplate = (rowData) => {
        if (rowData.isActive === 1) return <span className="p-badge p-badge-success">{th('laborCatalog.status.active')}</span>;
        if (rowData.isActive === 0) return <span className="p-badge p-badge-danger">{th('laborCatalog.status.inactive')}</span>;
        return <span className="p-badge p-badge-warning">{th('laborCatalog.status.cancelled')}</span>;
    };
    const sortedData = useMemo(() => {
        if (!sortField || sortOrder === 0) return normalizedData;

        return [...normalizedData].sort((a, b) => {
            let aValue = a[sortField];
            let bValue = b[sortField];

            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return sortOrder;
            if (bValue == null) return -sortOrder;

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (sortField.toLowerCase().includes('date') || sortField === 'sellDate') {
                aValue = new Date(aValue);
                bValue = new Date(bValue);
            }

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortOrder * (aValue - bValue);
            }

            if (aValue < bValue) return -sortOrder;
            if (aValue > bValue) return sortOrder;
            return 0;
        });
    }, [normalizedData, sortField, sortOrder]);

    const handleSort = (e) => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder);
    };
    const descriptionBodyTemplate = (rowData) => {
        if (rowData.translations && rowData.translations.length > 0) {
            return getTranslationText(rowData.translations, ' ');
        }
        // Fallback: Eski sistem için TR/EN alanları
        const currentLangId = getCurrentLanguageId();
        if (currentLangId === 2) return rowData.laborDescriptionTR || '-'; // TR
        if (currentLangId === 1) return rowData.laborDescriptionEN || '-'; // EN
        return ' ';
    };

    const getColumnsCallback = useCallback((el) => {
        if (el && el.getColumns && !columnsRef.current) {
            columnsRef.current = el.getColumns();
        }
        dataTableRef.current = el;
    }, []);

    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2 justify-content-center">
            <i className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} onClick={() => onEdit(rowData)} />
            <i className="pi pi-trash cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} onClick={() => onDelete(rowData.id)} />
            <i className="pi pi-history cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('warrantyClaims.tooltips.history')}></i>
        </div>
    );

    return (
        <DataTable
            className="p-datatable-gridlines"
            paginator
            value={sortedData}
            rows={lazyParams.rows || 10}
            loading={loading}
            globalFilterFields={['productGroup', 'carItemCode', 'model', 'createdDate']}
            totalRecords={totalCount}
            showGridlines
            lazy
            first={lazyParams.first}
            onPage={onPage}
            onSort={handleSort}
            sortField={sortField}
            sortOrder={sortOrder}
            dataKey="id"
            filterDisplay="menu"
            emptyMessage={th('laborCatalog.table.emptyMessage')}
            rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]}
            tableStyle={{ minWidth: '60rem' }}
            ref={getColumnsCallback}
            sortMode="single"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        >
            <Column body={actionBodyTemplate} style={{ width: '5%' }} align="center" />
            <Column sortable field="startDate" header={th('laborCatalog.table.startDate')} body={(rowData) => dateOnlyBodyTemplate(rowData, 'startDate')} />
            <Column sortable field="productGroup" header={th('laborCatalog.table.productGroup')} />
            <Column sortable field="model" header={th('laborCatalog.table.model')} />
            <Column sortable field="carItemCode" header={th('laborCatalog.table.carItemCode')} />
            <Column sortable field="translationTextLaborGroup" header={th('laborCatalog.table.laborGroup')} />
            <Column sortable field="laborCode" header={th('laborCatalog.table.laborCode')} />
            <Column sortable field="description" header={th('laborCatalog.table.description')} body={descriptionBodyTemplate} />
            <Column sortable field="laborTime" header={th('laborCatalog.table.laborTime')} />
            <Column sortable field="status" header={th('laborCatalog.table.status')} body={statusBodyTemplate} />
            <Column sortable field="createdDate" header={th('laborCatalog.table.createdDate')} body={(rowData) => dateOnlyBodyTemplate(rowData, 'createdDate')} />
        </DataTable>
    );
};

export default LaborCatalogTable;
